import React, { useCallback, useEffect, useRef, useState } from 'react';

const TURNSTILE_SITE_KEY = '0x4AAAAAADM5ULu6WlpGoiJU';

let turnstileLoad;

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);

  if (!turnstileLoad) {
    turnstileLoad = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-turnstile-api]');
      if (existing) {
        existing.addEventListener('load', () => resolve(window.turnstile), { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.turnstileApi = 'true';
      script.onload = () => resolve(window.turnstile);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  return turnstileLoad;
}

async function revealEmail(token) {
  const response = await fetch('/api/reveal-email', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      target: 'home',
      token,
    }),
  });

  if (!response.ok) {
    throw new Error('email verification failed');
  }

  return response.json();
}

function Contact() {
  const widgetRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [status, setStatus] = useState('idle');
  const [mailHref, setMailHref] = useState('');

  const resetWidget = useCallback(() => {
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }
  }, []);

  const handleToken = useCallback(async (token) => {
    setStatus('checking');

    try {
      const result = await revealEmail(token);
      setMailHref(result.href);
      setStatus('ready');
    } catch {
      setMailHref('');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (status !== 'verifying') return undefined;

    let cancelled = false;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !widgetRef.current || widgetIdRef.current !== null) return;

        widgetIdRef.current = turnstile.render(widgetRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: handleToken,
          'error-callback': () => setStatus('error'),
          'expired-callback': () => setStatus('verifying'),
          size: 'compact',
          theme: 'light',
        });
      })
      .catch(() => setStatus('error'));

    return () => {
      cancelled = true;
      resetWidget();
    };
  }, [handleToken, resetWidget, status]);

  const beginVerification = () => {
    resetWidget();
    setMailHref('');
    setStatus('verifying');
  };

  return (
    <div className="footer">
      {status === 'ready' ? (
        <a href={mailHref} className="contact" aria-label="Open Contact Email">
          Open email
        </a>
      ) : (
        <button type="button" className="contact" aria-label="Verify Contact Email" onClick={beginVerification} disabled={status === 'checking'}>
          {status === 'checking' ? 'Checking' : status === 'error' ? 'Retry' : 'Contact'}
        </button>
      )}
      {(status === 'verifying' || status === 'checking' || status === 'error') && (
        <div className="contact-gate" role="dialog" aria-label="Email verification">
          {status === 'verifying' && <div ref={widgetRef} className="turnstile-widget" />}
          {status === 'checking' && <span className="contact-gate-status">Checking</span>}
          {status === 'error' && (
            <button type="button" className="contact-gate-action" onClick={beginVerification}>
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default Contact;
